import { UserId } from "@custom-types/base-types";
import {
  MemberRoleTypes,
  PartialSdbMemberTypes,
  SdbMemberTypes,
  UserRoleEnum,
} from "@custom-types/member-types";
import { EmailUtils } from "@stellar/utils";
import { StringEntityId } from "@store/store-types";
import { getPrettyName } from "@utils/user-utils";

/** Gets the ID for a member to be used in the entities dictionary to identify each member  */
export function getMemberId(member: PartialSdbMemberTypes): StringEntityId {
  return member.id ?? member.identity ?? member.email ?? "";
}

/**
 * Check the input text and return an array of valid emails if available
 * Splits by comma, semicolon, spaces or tabs
 */
export function parseTextToEmails(inputText: string): string[] {
  const emails: string[] = [];
  if (/[,\s;]/.test(inputText)) {
    const possibleEmails = inputText.trim().split(/[,\s;]+/);

    possibleEmails.forEach((possibleEmail) => {
      const email = possibleEmail.trim();
      if (EmailUtils.isValidEmail(email)) {
        emails.push(email);
      }
    });
  }
  return emails;
}

interface GetMemberByIdProps {
  /** ID of the member */
  memberId?: string;

  /** List of company members */
  companyMembers: SdbMemberTypes[];

  /** List of project members */
  projectMembers: SdbMemberTypes[];
}

/** Returns the member entity by the member ID if it's available */
export function getMemberById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): SdbMemberTypes | undefined {
  const companyMember = companyMembers.find(
    (member) => member.identity === memberId
  );

  const projectMember = projectMembers.find(
    (member) => member.identity === memberId
  );

  // Give priority to the company level member entity
  return companyMember || projectMember;
}

/** Returns the get the formatted member name by the member ID if it's available */
export function getMemberNameById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): string | undefined {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers,
  });

  const name = getPrettyName(member);

  return name !== "" ? name : undefined;
}

/** Returns the email by the member ID if it's available */
export function getMemberEmailById({
  memberId,
  companyMembers,
  projectMembers,
}: GetMemberByIdProps): string | undefined {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers,
  });

  return member?.email;
}

/** Returns a map of members accessible by id */
export function getMembersMapById(
  companyMembers: SdbMemberTypes[]
): Map<UserId, SdbMemberTypes> {
  return new Map(companyMembers.map((member) => [member.identity, member]));
}

/** Returns the higher role between two role provided */
export function getHigherRole(
  roleA: MemberRoleTypes,
  roleB: MemberRoleTypes
): MemberRoleTypes {
  return UserRoleEnum[roleA] >= UserRoleEnum[roleB] ? roleA : roleB;
}
